import { isNil } from "lodash";
import { CalendarMode, Row } from "PFApp/booking/types";
import { ProjectItemResource } from "PFApp/booking/types/row";
import { PER_PAGE_UNPAGINATED } from "PFApp/constants/unpaginated";
import { getValueFilters } from "PFApp/use_filtered_collection";
import { Filters, Id, Value } from "PFTypes";

import { getOriginalResource } from "../../helpers";
import { RowChildrenItem } from "./queries/use_row_children";

export const PER_PAGE = 50;

export type RequestData = {
  url: string;
  params: { perPage: number; filters?: Filters<Value> } & Record<string, any>;
  options?: Record<string, any>;
  childrenType: string | null;
  unpaginated?: boolean;
  mapResponse?: (response: RowChildrenItem) => RowChildrenItem;
};

const appendOtherRowToLastPage =
  (id: Id) =>
  (response: RowChildrenItem): RowChildrenItem => {
    const { meta } = response;
    if (meta.page !== meta.totalPages) {
      return response;
    }
    return {
      ...response,
      meta: {
        ...meta,
        total: (meta.total || 0) + 1
      },
      entries: [
        ...response.entries,
        {
          id: `other_${id}`,
          other: true
        }
      ]
    };
  };

const engagementChildRolesRequest = (id: Id, filters: Filters<Value> = {}): RequestData => ({
  url: "/api/booking_module/roles/index",
  options: {
    method: "POST",
    stringify: true,
    contentType: "application/json; charset=utf-8"
  },
  params: {
    perPage: PER_PAGE,
    filters: {
      ...filters,
      fields: {
        ...(filters.fields || {}),
        parent_activity_id: id
      }
    },
    order: {
      name: ["activity.saas_created_at"],
      text: "Latest first",
      direction: "desc"
    }
  },
  childrenType: "role"
});

const profilesLinkedToEngagementRequest = (id: Id): RequestData => ({
  url: "/api/booking_module/activity_profiles",
  params: { perPage: PER_PAGE_UNPAGINATED, activity_id: id },
  childrenType: "profile",
  unpaginated: true
});

const roleProfilesRequest = (id: Id): RequestData => ({
  url: "/api/booking_module/activity_profiles",
  params: { perPage: PER_PAGE, activity_id: id },
  childrenType: "profile"
});

const profileRolesRequest = (id: Id): RequestData => ({
  url: "/api/booking_module/booked_activities",
  params: { perPage: PER_PAGE, profile_id: id },
  childrenType: "role",
  mapResponse: appendOtherRowToLastPage(id)
});

export const getRequestsData = (mode: CalendarMode, row: Row, filters?: Filters): RequestData[] => {
  const { type } = row;
  const original = getOriginalResource(row, mode);
  const id = original?.id;
  if (isNil(id)) {
    return [];
  }
  switch (type) {
    case "engagement": {
      const { children_workflow_states_tally: childrenStatesTally } = original as ProjectItemResource;
      const roleFilters = getValueFilters(filters?.children);
      return [
        ...(childrenStatesTally ? [engagementChildRolesRequest(id, roleFilters)] : []),
        profilesLinkedToEngagementRequest(id)
      ];
    }
    case "role":
      return mode === CalendarMode.Projects ? [roleProfilesRequest(id)] : [];
    case "profile":
      return mode === CalendarMode.Workforce ? [profileRolesRequest(id)] : [];
    default:
      return [];
  }
};
